import { z } from 'zod';
import { LABEL_TYPES } from '@/shared/types/label.types';
import {
  PRINT_FAILURE_CODES,
  PRINT_LABEL_KINDS,
  PRINT_ORDER_SOURCES,
  PRINT_ORDER_STATUSES,
  PRINT_QUEUE_ITEM_STATUSES,
} from '@/shared/types/printOrder.types';

const WarehouseLabelTypeSchema = z.enum(LABEL_TYPES);
const WarehouseLabelTypeOrEmptySchema = z.union([WarehouseLabelTypeSchema, z.literal('')]);

// --- Common response schemas ---

export const ApiMessageResultSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  printed: z.boolean().optional(),
  storageId: z.string().optional(),
  storageIdPrefix: z.string().optional(),
});

// --- Inventory Counts ---

export const WorkOrderItemSchema = z.object({
  id: z.union([z.number(), z.string()]),
  sku: z.string(),
  type: z.string(),
  currentInventory: z.number().optional(),
  actualInventory: z.number().nullable().optional(),
  countComplete: z.boolean().optional(),
  createdAt: z.string().optional(),
});

export const WorkOrdersSummarySchema = z.object({
  totalWorkOrders: z.number().optional(),
  criticalAlerts: z.number().optional(),
  lowStockAlerts: z.number().optional(),
  cycleCounts: z.number().optional(),
  cached: z.boolean().optional(),
});

export const WorkOrdersResponseSchema = z.object({
  alerts: z.array(WorkOrderItemSchema),
  cycleCounts: z.array(WorkOrderItemSchema),
  manualRequests: z.array(WorkOrderItemSchema).optional(),
  summary: WorkOrdersSummarySchema.optional(),
});

export const CycleCountDetailsSchema = z.object({
  lastCountDate: z.string().nullable().optional(),
  countClass: z.string().optional(),
});

export const RecentAlertItemSchema = z.object({
  id: z.union([z.number(), z.string()]),
  sku: z.string(),
  type: z.string(),
  expectedInventory: z.number().nullable().optional(),
  actualInventory: z.number().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
});

export const RecentCountHistoryItemSchema = z.object({
  id: z.union([z.number(), z.string()]),
  type: z.string(),
  expectedInventory: z.number().nullable().optional(),
  actualInventory: z.number().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
});

export const BundleLocationRowSchema = z.object({
  bundleSku: z.string(),
  type: z.string(),
  shelfIndex: z.string(),
});

export const SubmitCycleCountResponseSchema = z.object({
  success: z.boolean(),
  data: WorkOrderItemSchema,
  shopifyUpdated: z.boolean(),
});

// --- Shelf Index ---

export const ShelfLocationSchema = z.object({
  shelf_index: z.string(),
  type: WarehouseLabelTypeOrEmptySchema,
  id: z.string(),
  is_primary: z.boolean().optional(),
});

export const ShelfItemSchema = z.object({
  sku: z.string(),
  type: WarehouseLabelTypeOrEmptySchema,
  id: z.string(),
  shelf_index: z.string(),
});

export const CheckOutByShelfResponseSchema = z.object({
  success: z.literal(true),
  checkedOut: z.number(),
});

export const AssignBatchFailureSchema = z.object({
  prefix: z.string(),
  message: z.string(),
});

export const AssignBatchResponseSchema = z.object({
  success: z.boolean(),
  assigned: z.number(),
  failed: z.array(AssignBatchFailureSchema),
});

export const LabelStatusSchema = z.object({
  found: z.boolean(),
  checkedIn: z.boolean(),
  createdAt: z.string().nullable().optional(),
});

export const LabelLookupSchema = z.object({
  storageId: z.string(),
  sku: z.string(),
  baseQty: z.number().nullable().optional(),
  type: WarehouseLabelTypeSchema.nullable().optional(),
  shelfIndex: z.string().nullable().optional(),
  isLongBox: z.boolean().optional().default(false),
});

export const BrowseIndexesEntrySchema = z.object({
  id: z.string(),
  sku: z.string(),
  shelf_index: z.string(),
  type: WarehouseLabelTypeOrEmptySchema,
});

export const BrowseIndexesResponseSchema = z.object({
  entries: z.array(BrowseIndexesEntrySchema),
  hasMore: z.boolean(),
});

export const BrowseCheckedOutIndexesEntrySchema = z.object({
  id: z.string(),
  sku: z.string(),
  shelf_index: z.string(),
  type: WarehouseLabelTypeOrEmptySchema,
  checked_out_at: z.string().nullable(),
});

export const BrowseCheckedOutIndexesResponseSchema = z.object({
  entries: z.array(BrowseCheckedOutIndexesEntrySchema),
  hasMore: z.boolean(),
});

// --- Print orders / print queue ---

const PrintLabelKindSchema = z.enum(PRINT_LABEL_KINDS);
const PrintOrderStatusSchema = z.enum(PRINT_ORDER_STATUSES);
const PrintQueueItemStatusSchema = z.enum(PRINT_QUEUE_ITEM_STATUSES);
const PrintFailureCodeSchema = z.enum(PRINT_FAILURE_CODES);
const PrintOrderSourceSchema = z.enum(PRINT_ORDER_SOURCES);

export const PrintQueueItemSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  seq: z.number(),
  sheetSeq: z.number(),
  sku: z.string().nullable(),
  shelfIndex: z.string().nullable(),
  baseQty: z.number().nullable(),
  storageId: z.string().nullable(),
  storageIdPrefix: z.string().nullable(),
  status: PrintQueueItemStatusSchema,
  printedAt: z.string().nullable(),
  failureReason: z.string().nullable(),
});

export const PrintOrderSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  labelKind: PrintLabelKindSchema,
  labelType: WarehouseLabelTypeSchema.nullable(),
  source: PrintOrderSourceSchema,
  skus: z.array(z.string()),
  shelfIndex: z.string().nullable(),
  shelfIndexes: z.array(z.string()),
  baseQty: z.number().nullable(),
  uniqueIdCount: z.number(),
  copiesPerId: z.number(),
  totalLabels: z.number(),
  printedLabels: z.number(),
  status: PrintOrderStatusSchema,
  success: z.boolean(),
  failureCode: PrintFailureCodeSchema.nullable(),
  failureReason: z.string().nullable(),
  retryOfOrderId: z.string().nullable(),
});

export const PrintOrderSheetSchema = z.object({
  sheetSeq: z.number(),
  items: z.array(PrintQueueItemSchema),
});

export const PrintOrderWithSheetsSchema = z.object({
  success: z.boolean().optional(),
  order: PrintOrderSchema,
  sheets: z.array(PrintOrderSheetSchema),
});

export const PrintOrderListRowSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  labelKind: PrintLabelKindSchema,
  uniqueIdCount: z.number(),
  totalLabels: z.number(),
  printedLabels: z.number(),
  unprintedLabels: z.number(),
  status: PrintOrderStatusSchema,
  success: z.boolean(),
  failureCode: PrintFailureCodeSchema.nullable(),
  failureReason: z.string().nullable(),
});

export const PrintOrderListResponseSchema = z.object({
  entries: z.array(PrintOrderListRowSchema),
  hasMore: z.boolean(),
});

export const PrintSheetResultSchema = z.object({
  success: z.boolean(),
  printed: z.boolean().optional(),
  alreadyPrinted: z.boolean().optional(),
  message: z.string().optional(),
  order: PrintOrderSchema.nullable().optional(),
});

export const CancelMissingResultSchema = z.object({
  success: z.boolean(),
  deletedStorageIds: z.array(z.string()).optional(),
  error: z.string().optional(),
});

// --- Orders ---

export const ShipstationOrderShipToSchema = z.object({
  name: z.string(),
  street1: z.string().optional(),
  street2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  phone: z.string().nullable().optional(),
});

export const ShipstationOrderSchema = z.object({
  orderId: z.union([z.string(), z.number()]),
  orderNumber: z.union([z.string(), z.number()]).optional(),
  customerName: z.string().optional(),
  shipTo: ShipstationOrderShipToSchema.optional(),
  tagIds: z.array(z.number()).nullable().optional(),
  items: z.array(z.object({
    sku: z.string().optional(),
    quantity: z.number().optional(),
    name: z.string().optional(),
    options: z.array(z.object({
      name: z.string().optional(),
      value: z.string().optional(),
    })).optional(),
  })).optional(),
});

export const ShipstationOrdersResponseSchema = z.object({
  orders: z.array(ShipstationOrderSchema),
});

export const ShipstationTagSchema = z.object({
  tagId: z.number(),
  name: z.string(),
});

// ShipStation tags endpoint returns a raw array, not { tags: [...] }
export const ShipstationTagsResponseSchema = z.array(ShipstationTagSchema);

export const FetchNextOrderResponseSchema = z.object({
  success: z.boolean().optional(),
  order: ShipstationOrderSchema.optional(),
  orders: z.array(ShipstationOrderSchema).optional(),
});

export const PrintNextOrderResponseSchema = z.object({
  success: z.boolean(),
  orderNumber: z.string(),
});

export const ClaimByLabelResponseSchema = z.object({
  success: z.boolean(),
  order: ShipstationOrderSchema,
});

export const AppSettingsResponseSchema = z.object({
  print_claim_window_minutes: z.number().int(),
});

export const ReleaseOrderResponseSchema = z.object({
  success: z.boolean(),
});

export const ReserveOrderResultSchema = z.object({
  orderId: z.string(),
  success: z.boolean(),
  skipped: z.boolean().optional(),
  takenByOther: z.boolean().optional(),
  error: z.string().optional(),
});

export const ReserveOrdersResponseSchema = z.object({
  success: z.boolean(),
  tagged: z.number(),
  skipped: z.number().optional(),
  takenByOther: z.number().optional(),
  total: z.number(),
  results: z.array(ReserveOrderResultSchema),
});

export const ReleaseBatchResponseSchema = z.object({
  success: z.boolean(),
  released: z.number(),
  total: z.number(),
  results: z.array(ReserveOrderResultSchema),
});

export const SingleSkuGroupOrderSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  customerName: z.string(),
  reservedBy: z.string().nullable(),
});

export const SingleSkuGroupSchema = z.object({
  sku: z.string(),
  count: z.number(),
  itemName: z.string(),
  orders: z.array(SingleSkuGroupOrderSchema),
});

export const SingleSkuSummaryResponseSchema = z.object({
  groups: z.array(SingleSkuGroupSchema),
});

export const SingleItemPackageWeightsSchema = z.object({
  sku: z.string(),
  length: z.number(),
  width: z.number(),
  height: z.number(),
  weightOz: z.number(),
});

export const ProductVariantWeightSchema = z.object({
  sku: z.string(),
  name: z.string(),
  weightOz: z.number().nullable(),
});

export const ShipBatchWeightSchema = z.object({
  value: z.number(),
  units: z.enum(['ounces', 'pounds']),
});

export const ShipBatchDimensionsSchema = z.object({
  length: z.number(),
  width: z.number(),
  height: z.number(),
  units: z.literal('inches'),
});

export const ShipBatchOrderResultSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  success: z.boolean(),
  trackingNumber: z.string().optional(),
  shipmentId: z.number().optional(),
  carrierCode: z.string().optional(),
  serviceCode: z.string().optional(),
  printed: z.boolean().optional(),
  existingLabel: z.boolean().optional(),
  error: z.string().optional(),
  labelData: z.string().optional(),
});

export const ShipBatchResponseSchema = z.object({
  success: z.boolean(),
  shipped: z.number(),
  total: z.number(),
  results: z.array(ShipBatchOrderResultSchema),
});

export const ShipLabelStatusSchema = z.object({
  hasPurchasedLabel: z.boolean(),
});

export const ReprintShipLabelResponseSchema = z.object({
  success: z.boolean(),
  printed: z.number(),
  total: z.number(),
  source: z.enum(['memory', 'shipstation']),
  error: z.string().optional(),
});

/** POST /api/orders/:orderId/split — Box 1's order number first, then each new box order. */
export const SplitOrderResponseSchema = z.object({
  success: z.literal(true),
  orderNumbers: z.array(z.string()),
});

// --- Product Warehouse Notes ---

export const ProductWarehouseNoteSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  sku: z.string(),
  note: z.string(),
  updated_at: z.string(),
});

// --- Bundle Scan Subset ---

export const BundleScanSubsetComponentSchema = z.object({
  sku: z.string(),
  quantity: z.number(),
  name: z.string().optional(),
});

export const BundleScanSubsetEntrySchema = z.object({
  id: z.number(),
  parentSku: z.string(),
  createdAt: z.string(),
  components: z.array(BundleScanSubsetComponentSchema),
});

export const BundleScanSubsetListResponseSchema = z.object({
  entries: z.array(BundleScanSubsetEntrySchema),
});

export const BundleScanSubsetAddResponseSchema = z.object({
  entry: BundleScanSubsetEntrySchema,
});

export const BundleScanSubsetRemoveResponseSchema = z.object({
  success: z.literal(true),
  id: z.number(),
});

export const BundleDefaultComponentSchema = z.object({
  id: z.number(),
  sku: z.string(),
  quantity: z.number(),
  name: z.string().optional(),
});

export const BundleDefaultListEntrySchema = z.object({
  parentSku: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  components: z.array(BundleDefaultComponentSchema),
});

export const BundleDefaultListResponseSchema = z.object({
  entries: z.array(BundleDefaultListEntrySchema),
});

export const BundleDefaultAddResponseSchema = z.object({
  entry: BundleDefaultListEntrySchema,
});

export const BundleDefaultDeleteResponseSchema = z.object({
  success: z.literal(true),
  parentSku: z.string(),
});

export const BundleDefaultComponentResponseSchema = z.object({
  component: BundleDefaultComponentSchema,
});

export const BundleDefaultComponentRemoveResponseSchema = z.object({
  success: z.literal(true),
  id: z.number(),
});

export const BundleFloorBinLevelSchema = z.object({
  storageId: z.string(),
  storageIdPrefix: z.string(),
  shelfIndex: z.string().nullable(),
  isPrimary: z.boolean(),
  quantity: z.number(),
});

export const BundleFloorLevelSchema = z.object({
  sku: z.string(),
  targetQty: z.number(),
  quantityOnFloor: z.number(),
  updatedAt: z.string(),
  version: z.number(),
  itemName: z.string().optional(),
  components: z.array(BundleScanSubsetComponentSchema).optional(),
  bins: z.array(BundleFloorBinLevelSchema).optional(),
});

export const BundleFloorListResponseSchema = z.object({
  entries: z.array(BundleFloorLevelSchema),
});

export const BundleFloorEntryResponseSchema = z.object({
  entry: BundleFloorLevelSchema,
});

export const BundleFloorRemoveResponseSchema = z.object({
  success: z.literal(true),
  sku: z.string(),
});

export const BundleFloorBinDeltaResultSchema = z.object({
  sku: z.string(),
  storageIdPrefix: z.string().nullable(),
  direction: z.enum(['increment', 'decrement', 'set_quantity']),
  quantity: z.number(),
  applied: z.boolean(),
  error: z.string().optional(),
});

export const BundleFloorBinDeltaBatchResponseSchema = z.object({
  results: z.array(BundleFloorBinDeltaResultSchema),
  entries: z.array(BundleFloorLevelSchema),
});

// --- Auth ---

export const AuthUserSchema = z.object({
  id: z.union([z.string(), z.number()]),
  userId: z.string().optional(),
  name: z.string(),
  email: z.string(),
});

export const AuthStatusResponseSchema = z.object({
  authenticated: z.boolean(),
  user: AuthUserSchema.optional(),
  isDev: z.boolean().optional(),
  isAdmin: z.boolean().optional(),
  role: z.enum(['user', 'admin', 'dev', 'owner']).optional(),
  receivingDataSource: z.enum(['airtable', 'dual', 'supabase']).optional(),
});

export const UserSettingsSchema = z.object({
  color_mode: z.boolean(),
  camera_opens_order_details: z.boolean(),
  collapse_located_bundles: z.boolean(),
  photograph_packing_items: z.boolean(),
  print_scan_order_label: z.boolean(),
  scan_suggest_box_size: z.boolean(),
});

export const OrderPickScanLineSchema = z.object({
  lineId: z.string(),
  sku: z.string().nullable(),
  storageIdPrefix: z.string().nullable(),
});

export const OrderPickScansResponseSchema = z.object({
  scans: z.array(OrderPickScanLineSchema),
});

export const OrderPickScansClearedResponseSchema = z.object({
  cleared: z.boolean(),
});

// --- OCR ---

export const OcrResponseSchema = z.object({
  text: z.string(),
});

export const OcrAbArmResultSchema = z.object({
  text: z.string().nullable(),
  latencyMs: z.number(),
});

export const OcrAbCompareResponseSchema = z.object({
  text: OcrAbArmResultSchema,
  document: OcrAbArmResultSchema,
});

// --- SKU ---

export const SkuExistsResponseSchema = z.object({
  sku: z.string(),
  exists: z.boolean(),
});

export const ComponentSkusResponseSchema = z.object({
  sku: z.string(),
  BillOfMaterialsProducts: z.object({
    productSKU: z.string(),
    productName: z.string(),
    productVariantId: z.union([z.string(), z.number()]),
  }),
});

// --- Shelves ---

export const AuditShelfSchema = z.object({
  id: z.number(),
  shelf_name: z.string(),
  audit_flag: z.union([z.boolean(), z.null()]),
  audited_at: z.string().nullable().optional(),
  performed_by: z.string().nullable().optional(),
  existing_ids: z.array(z.string()).optional(),
  label_count: z.number().optional(),
  claimed_by: z.string().nullable().optional(),
  claim_expires_at: z.string().nullable().optional(),
});

export const ShelfAuditSessionSchema = z.object({
  id: z.number(),
  seq: z.number(),
  name: z.string(),
  status: z.enum(['open', 'closed']),
  started_by: z.string(),
  started_at: z.string(),
  ended_by: z.string().nullable(),
  ended_at: z.string().nullable(),
});

export const AuditShelfResponseSchema = z.object({
  shelves: z.array(AuditShelfSchema),
  completion: z.object({
    audited: z.number(),
    total: z.number(),
    percent: z.number(),
  }),
  audit: ShelfAuditSessionSchema.nullable(),
});

export const UpdateShelfAuditResponseSchema = z.object({
  shelf: AuditShelfSchema,
});

export const ResetShelfAuditResponseSchema = z.object({
  success: z.literal(true),
  updated: z.number(),
});

export const StartShelfAuditResponseSchema = z.object({
  audit: ShelfAuditSessionSchema,
});

export const EndShelfAuditResponseSchema = z.object({
  audit: ShelfAuditSessionSchema,
});

export const CreateShelfAuditEventResponseSchema = z.object({
  success: z.literal(true),
  event: z.object({
    id: z.number(),
    created_at: z.string(),
  }),
});

export const ClaimShelfAuditResponseSchema = z.object({
  shelf: AuditShelfSchema,
});

export const ReleaseShelfAuditResponseSchema = z.object({
  success: z.literal(true),
});

export const ClaimNextShelfAuditResponseSchema = z.object({
  shelf: AuditShelfSchema.nullable(),
});

// --- Dev ---

export const StorjBackupObjectSchema = z.object({
  key: z.string(),
  lastModified: z.string().nullable(),
  size: z.number(),
});

export const StorjBackupListResponseSchema = z.object({
  objects: z.array(StorjBackupObjectSchema),
});

export const PreviewLabelResponseSchema = z.object({
  success: z.literal(true),
  images: z.array(z.string().min(1)).min(1),
});

export const PrinterProbeQueryResultSchema = z.object({
  command: z.string(),
  what: z.string(),
  answered: z.boolean(),
  hex: z.string(),
  ascii: z.string(),
});

export const PrinterStatusProbeResponseSchema = z.object({
  success: z.literal(true),
  printerId: z.string(),
  printerIp: z.string(),
  queries: z.array(PrinterProbeQueryResultSchema),
});

export const PosLabelFormatSummarySchema = z.object({
  shop: z.string(),
  updatedAt: z.string(),
  widthIn: z.number(),
  heightIn: z.number(),
  dpi: z.number(),
  fieldTypes: z.array(z.enum(['productName', 'price', 'barcode', 'sku'])),
});

export const PosLabelFormatsResponseSchema = z.object({
  formats: z.array(PosLabelFormatSummarySchema),
});

export const PrintPosLabelFormatResponseSchema = z.object({
  success: z.literal(true),
  shop: z.string(),
  printerId: z.string(),
  printerIp: z.string(),
});

// --- Dashboard ---

export const DashboardDailyOrderCountSchema = z.object({
  date: z.string(),
  count: z.number(),
});

export const DashboardUserOrderStatsSchema = z.object({
  userTagId: z.number(),
  userName: z.string(),
  totalOrders: z.number(),
  activeDays: z.number(),
  avgOrdersPerActiveDay: z.number(),
  dailyCounts: z.array(DashboardDailyOrderCountSchema),
});

export const DashboardOrdersPerUserResponseSchema = z.object({
  start: z.string(),
  end: z.string(),
  timeZone: z.string(),
  users: z.array(DashboardUserOrderStatsSchema),
});

export const ActivityUserDailyRowSchema = z.object({
  day: z.string(),
  userId: z.string(),
  userName: z.string(),
  scans: z.number(),
  failedScans: z.number(),
  submits: z.number(),
  ordersWorked: z.number(),
  assignedPickNext: z.number(),
  activeSeconds: z.number(),
  linesSubmitted: z.number(),
  unitsSubmitted: z.number(),
});

export const ActivityOrderCycleRowSchema = z.object({
  orderId: z.string(),
  userId: z.string(),
  userName: z.string(),
  submittedAt: z.string(),
  firstScanAt: z.string().nullable(),
  cycleSeconds: z.number().nullable(),
  lineCount: z.number().nullable(),
  unitCount: z.number().nullable(),
  secondsPerLine: z.number().nullable(),
  assignmentSource: z.enum(['pick_next', 'manual']),
  gapSecondsBefore: z.number().nullable(),
});

export const ActivityDataQualityCheckSchema = z.object({
  checkName: z.string(),
  value: z.number(),
});

export const ActivitySlaDailyRowSchema = z.object({
  queueDate: z.string(),
  ordersTotal: z.number(),
  expeditedTotal: z.number(),
  metSameDay: z.number(),
  attainmentPct: z.number().nullable(),
  openRemaining: z.number(),
  openOrderNumbers: z.array(z.string()),
  queueClearAt: z.string().nullable(),
});

export const DashboardActivityResponseSchema = z.object({
  start: z.string(),
  end: z.string(),
  timeZone: z.string(),
  daily: z.array(ActivityUserDailyRowSchema),
  cycles: z.array(ActivityOrderCycleRowSchema),
  dataQuality: z.array(ActivityDataQualityCheckSchema),
  sla: z.array(ActivitySlaDailyRowSchema),
});

export const PastPerformanceDailyRowSchema = z.object({
  date: z.string(),
  ordersShipped: z.number(),
  tagCounts: z.record(z.string(), z.number()),
  earliestShipTime: z.string().nullable(),
  latestShipTime: z.string().nullable(),
});

export const PastPerformanceAveragesSchema = z.object({
  activeDays: z.number(),
  avgOrdersPerDay: z.number(),
  avgEarliestShipTime: z.string().nullable(),
  avgLatestShipTime: z.string().nullable(),
  avgTagCounts: z.record(z.string(), z.number()),
});

export const PastPerformanceResponseSchema = z.object({
  start: z.string(),
  end: z.string(),
  tags: z.array(z.string()),
  daily: z.array(PastPerformanceDailyRowSchema),
  averages: PastPerformanceAveragesSchema,
});

export const PastPerformanceProgressEventSchema = z.object({
  current: z.number(),
  total: z.number(),
  message: z.string(),
});

export const FilterSweepRunSchema = z.object({
  id: z.string().min(1),
  finishedAt: z.string().min(1),
  status: z.enum(['complete', 'failed']),
  environment: z.string().nullable(),
  trigger: z.string().min(1),
  durationMs: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  processed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errorMessage: z.string().nullable(),
});

export const FilterSweepRunsResponseSchema = z.object({
  runs: z.array(FilterSweepRunSchema),
});

export const InventoryTransferLineSummarySchema = z.object({
  id: z.string(),
  sku: z.string(),
  quantity: z.number(),
});

export const InventoryTransferListItemSchema = z.object({
  id: z.string(),
  transferNumber: z.number(),
  name: z.string(),
  startLocationName: z.string(),
  endLocationName: z.string(),
  requestorName: z.string(),
  submittedAt: z.string(),
  createdAt: z.string(),
  requestedDueDate: z.string(),
  completedAt: z.string().nullable(),
  lines: z.array(InventoryTransferLineSummarySchema),
  scannedLineIds: z.array(z.string()),
});

export const InventoryTransferListResponseSchema = z.object({
  requests: z.array(InventoryTransferListItemSchema),
});

export const InventoryTransferPickLineSchema = z.object({
  id: z.string(),
  sku: z.string(),
  quantity: z.number(),
  requestedQuantity: z.number(),
  productName: z.string(),
  displayName: z.string(),
});

export const InventoryTransferLineEventSchema = z.object({
  id: z.string(),
  lineId: z.string(),
  eventType: z.enum(['sku_substituted', 'quantity_changed']),
  sku: z.string().nullable(),
  previousSku: z.string().nullable(),
  newSku: z.string().nullable(),
  previousQuantity: z.number().nullable(),
  newQuantity: z.number().nullable(),
  actorName: z.string(),
  createdAt: z.string(),
});

export const InventoryTransferDetailSchema = z.object({
  id: z.string(),
  transferNumber: z.number(),
  name: z.string(),
  startLocationName: z.string(),
  endLocationName: z.string(),
  requestorName: z.string(),
  submittedAt: z.string(),
  createdAt: z.string(),
  requestedDueDate: z.string(),
  completedAt: z.string().nullable(),
  lines: z.array(InventoryTransferPickLineSchema),
  scannedLineIds: z.array(z.string()),
  lineEvents: z.array(InventoryTransferLineEventSchema),
});

export const InventoryTransferDetailResponseSchema = z.object({
  request: InventoryTransferDetailSchema,
});

export const InventoryTransferScanResponseSchema = z.object({
  lineId: z.string(),
  alreadyScanned: z.boolean(),
});

export const InventoryTransferCompleteResponseSchema = z.object({
  completedAt: z.string(),
});

export const InventoryTransferStoreLabelResponseSchema = z.object({
  success: z.literal(true),
  printedSkus: z.number(),
  printedLabels: z.number(),
  printerIp: z.string(),
  quantity: z.number(),
});

const InventoryTransferLineUpdateSchema = z.object({
  event: InventoryTransferLineEventSchema,
  line: InventoryTransferPickLineSchema.nullable(),
});

export const InventoryTransferLineChangeResponseSchema = InventoryTransferLineUpdateSchema.extend({
  merged: z.array(InventoryTransferLineUpdateSchema).default([]),
});

// --- Inferred types (replace manual interfaces) ---

export type ApiMessageResult = z.infer<typeof ApiMessageResultSchema>;
export type WorkOrderItem = z.infer<typeof WorkOrderItemSchema>;
export type WorkOrdersSummary = z.infer<typeof WorkOrdersSummarySchema>;
export type WorkOrdersResponse = z.infer<typeof WorkOrdersResponseSchema>;
export type CycleCountDetails = z.infer<typeof CycleCountDetailsSchema>;
export type RecentAlertItem = z.infer<typeof RecentAlertItemSchema>;
export type RecentCountHistoryItem = z.infer<typeof RecentCountHistoryItemSchema>;
export type BundleLocationRow = z.infer<typeof BundleLocationRowSchema>;
export type ShelfLocation = z.infer<typeof ShelfLocationSchema>;
export type ShelfItem = z.infer<typeof ShelfItemSchema>;
export type CheckOutByShelfResponse = z.infer<typeof CheckOutByShelfResponseSchema>;
export type AssignBatchResponse = z.infer<typeof AssignBatchResponseSchema>;
export type LabelStatus = z.infer<typeof LabelStatusSchema>;
export type LabelLookup = z.infer<typeof LabelLookupSchema>;
export type BrowseIndexesEntry = z.infer<typeof BrowseIndexesEntrySchema>;
export type BrowseIndexesResponse = z.infer<typeof BrowseIndexesResponseSchema>;
export type BrowseCheckedOutIndexesEntry = z.infer<typeof BrowseCheckedOutIndexesEntrySchema>;
export type BrowseCheckedOutIndexesResponse = z.infer<typeof BrowseCheckedOutIndexesResponseSchema>;
export type PrintQueueItemResponse = z.infer<typeof PrintQueueItemSchema>;
export type PrintOrderResponse = z.infer<typeof PrintOrderSchema>;
export type PrintOrderWithSheetsResponse = z.infer<typeof PrintOrderWithSheetsSchema>;
export type PrintOrderListRowResponse = z.infer<typeof PrintOrderListRowSchema>;
export type PrintOrderListResponse = z.infer<typeof PrintOrderListResponseSchema>;
export type PrintSheetResult = z.infer<typeof PrintSheetResultSchema>;
export type CancelMissingResult = z.infer<typeof CancelMissingResultSchema>;
export type ShipstationOrder = z.infer<typeof ShipstationOrderSchema>;
export type ShipstationOrdersResponse = z.infer<typeof ShipstationOrdersResponseSchema>;
export type ShipstationTagsResponse = z.infer<typeof ShipstationTagsResponseSchema>;
export type FetchNextOrderResponse = z.infer<typeof FetchNextOrderResponseSchema>;
export type PrintNextOrderResponse = z.infer<typeof PrintNextOrderResponseSchema>;
export type ClaimByLabelResponse = z.infer<typeof ClaimByLabelResponseSchema>;
export type AppSettingsResponse = z.infer<typeof AppSettingsResponseSchema>;
export type ReleaseOrderResponse = z.infer<typeof ReleaseOrderResponseSchema>;
export type ReserveOrdersResponse = z.infer<typeof ReserveOrdersResponseSchema>;
export type ReleaseBatchResponse = z.infer<typeof ReleaseBatchResponseSchema>;
export type SingleSkuGroup = z.infer<typeof SingleSkuGroupSchema>;
export type SingleSkuSummaryResponse = z.infer<typeof SingleSkuSummaryResponseSchema>;
export type SingleItemPackageWeights = z.infer<typeof SingleItemPackageWeightsSchema>;
export type ShipBatchResponse = z.infer<typeof ShipBatchResponseSchema>;
export type ShipBatchOrderResult = z.infer<typeof ShipBatchOrderResultSchema>;
export type ShipLabelStatus = z.infer<typeof ShipLabelStatusSchema>;
export type ReprintShipLabelResponse = z.infer<typeof ReprintShipLabelResponseSchema>;
export type SplitOrderResponse = z.infer<typeof SplitOrderResponseSchema>;
export type ProductWarehouseNote = z.infer<typeof ProductWarehouseNoteSchema>;
export type BundleScanSubsetComponent = z.infer<typeof BundleScanSubsetComponentSchema>;
export type BundleScanSubsetEntry = z.infer<typeof BundleScanSubsetEntrySchema>;
export type BundleDefaultComponent = z.infer<typeof BundleDefaultComponentSchema>;
export type BundleDefaultListEntry = z.infer<typeof BundleDefaultListEntrySchema>;
export type BundleFloorLevel = z.infer<typeof BundleFloorLevelSchema>;
export type BundleFloorBinLevel = z.infer<typeof BundleFloorBinLevelSchema>;
export type BundleFloorBinDeltaResult = z.infer<typeof BundleFloorBinDeltaResultSchema>;
export type BundleFloorBinDeltaBatch = z.infer<typeof BundleFloorBinDeltaBatchResponseSchema>;
export type AuthUser = z.infer<typeof AuthUserSchema>;
export type AuthStatusResponse = z.infer<typeof AuthStatusResponseSchema>;
export type UserSettingsResponse = z.infer<typeof UserSettingsSchema>;
export type OcrResponse = z.infer<typeof OcrResponseSchema>;
export type OcrAbArmResult = z.infer<typeof OcrAbArmResultSchema>;
export type OcrAbCompareResponse = z.infer<typeof OcrAbCompareResponseSchema>;
export type SkuExistsResponse = z.infer<typeof SkuExistsResponseSchema>;
export type ComponentSkusResponse = z.infer<typeof ComponentSkusResponseSchema>;
export type AuditShelf = z.infer<typeof AuditShelfSchema>;
export type ShelfAuditSession = z.infer<typeof ShelfAuditSessionSchema>;
export type AuditShelfResponse = z.infer<typeof AuditShelfResponseSchema>;
export type UpdateShelfAuditResponse = z.infer<typeof UpdateShelfAuditResponseSchema>;
export type ResetShelfAuditResponse = z.infer<typeof ResetShelfAuditResponseSchema>;
export type StartShelfAuditResponse = z.infer<typeof StartShelfAuditResponseSchema>;
export type EndShelfAuditResponse = z.infer<typeof EndShelfAuditResponseSchema>;
export type CreateShelfAuditEventResponse = z.infer<typeof CreateShelfAuditEventResponseSchema>;
export type ClaimShelfAuditResponse = z.infer<typeof ClaimShelfAuditResponseSchema>;
export type ReleaseShelfAuditResponse = z.infer<typeof ReleaseShelfAuditResponseSchema>;
export type ClaimNextShelfAuditResponse = z.infer<typeof ClaimNextShelfAuditResponseSchema>;
export type StorjBackupObject = z.infer<typeof StorjBackupObjectSchema>;
export type StorjBackupListResponse = z.infer<typeof StorjBackupListResponseSchema>;
export type PreviewLabelResponse = z.infer<typeof PreviewLabelResponseSchema>;
export type PrinterProbeQueryResult = z.infer<typeof PrinterProbeQueryResultSchema>;
export type PrinterStatusProbeResponse = z.infer<typeof PrinterStatusProbeResponseSchema>;
export type PosLabelFormatSummary = z.infer<typeof PosLabelFormatSummarySchema>;
export type PosLabelFormatsResponse = z.infer<typeof PosLabelFormatsResponseSchema>;
export type PrintPosLabelFormatResponse = z.infer<typeof PrintPosLabelFormatResponseSchema>;
export type DashboardDailyOrderCount = z.infer<typeof DashboardDailyOrderCountSchema>;
export type DashboardUserOrderStats = z.infer<typeof DashboardUserOrderStatsSchema>;
export type DashboardOrdersPerUserResponse = z.infer<typeof DashboardOrdersPerUserResponseSchema>;
export type ActivityUserDailyRow = z.infer<typeof ActivityUserDailyRowSchema>;
export type ActivityOrderCycleRow = z.infer<typeof ActivityOrderCycleRowSchema>;
export type ActivityDataQualityCheck = z.infer<typeof ActivityDataQualityCheckSchema>;
export type ActivitySlaDailyRow = z.infer<typeof ActivitySlaDailyRowSchema>;
export type DashboardActivityResponse = z.infer<typeof DashboardActivityResponseSchema>;
export type PastPerformanceDailyRow = z.infer<typeof PastPerformanceDailyRowSchema>;
export type PastPerformanceAverages = z.infer<typeof PastPerformanceAveragesSchema>;
export type PastPerformanceResponse = z.infer<typeof PastPerformanceResponseSchema>;
export type PastPerformanceProgressEvent = z.infer<typeof PastPerformanceProgressEventSchema>;
export type FilterSweepRun = z.infer<typeof FilterSweepRunSchema>;
export type FilterSweepRunsResponse = z.infer<typeof FilterSweepRunsResponseSchema>;
export type InventoryTransferListItem = z.infer<typeof InventoryTransferListItemSchema>;
export type InventoryTransferDetail = z.infer<typeof InventoryTransferDetailSchema>;
export type InventoryTransferScanResponse = z.infer<typeof InventoryTransferScanResponseSchema>;
export type InventoryTransferCompleteResponse = z.infer<typeof InventoryTransferCompleteResponseSchema>;
export type InventoryTransferStoreLabelResponse = z.infer<typeof InventoryTransferStoreLabelResponseSchema>;
export type InventoryTransferPickLine = z.infer<typeof InventoryTransferPickLineSchema>;
export type InventoryTransferLineEvent = z.infer<typeof InventoryTransferLineEventSchema>;
export type InventoryTransferLineChangeResponse = z.infer<typeof InventoryTransferLineChangeResponseSchema>;
